import classNames from '@/common/utils/classNames';
import { getDoctorProfile } from '@/common/apis/services/doctor/profile';
import { NativePanelKey, NATIVE_PANEL_TITLES } from '@/modules/doctorPanel/constants';
import { PANEL_KEY_SCOPE } from '@/modules/doctorPanel/secretary/scopes';
import { clearDoctorHomeShell } from '../utils/doctorHomeShell';
import { clearPersistedSelectedCenter } from '../store/selectedCenter';
import { setActingDoctor, useActingDoctor, useActingScopes } from '@/modules/doctorPanel/secretary/gateway';
import { useSecretarySession } from '@/modules/doctorPanel/secretary/useSecretarySession';
import { PanelCenterSelect } from '@/modules/doctorPanel/components/centerSelect';
import { useUserInfoStore } from '@/modules/login/store/userInfo';
import { useQuery } from '@tanstack/react-query';
import moment from 'jalali-moment';
import { CalendarDays, CalendarOff, Check, ChevronLeft, Clock, Stethoscope, type LucideIcon } from 'lucide-react';
import dynamic from 'next/dynamic';
import { ComponentType, useState } from 'react';
import { DsCard, DsSectionHeader, ds } from '../designSystem';
import { useSheetDrawerProps } from '../hooks/doctorHomeSheetLayout';
import { useSheetRoute } from '../hooks/useSheetRoute';
import { useSelectedCenterStore } from '../store/selectedCenter';
import { useSelectedDateStore } from '../store/selectedDate';
import { getDoctorCenterOptions } from '../utils/centers';
import { DsDrawer } from './DsDrawer';
import { FeedAppointmentsSection } from './feedAppointmentsSection';
import { FeedGreeting } from './feedGreeting';
import { FeedMotion } from './feedMotion';

/** the same native pages the doctor's own actions open, shown in a sheet over the home exactly like the doctor's */
const SHEET_PAGES: Record<'appointments' | 'workhours' | 'vacation', ComponentType> = {
  appointments: dynamic(() => import('@/modules/doctorPanel/pages/myPatients'), { ssr: false }),
  workhours: dynamic(() => import('@/modules/doctorPanel/pages/workhours'), { ssr: false }),
  vacation: dynamic(() => import('@/modules/doctorPanel/pages/vacation'), { ssr: false }),
};

const ACTIONS: { key: keyof typeof SHEET_PAGES; icon: LucideIcon; title: string; subtitle: string }[] = [
  { key: 'appointments', icon: CalendarDays, title: 'مراجعین من', subtitle: 'نوبت‌ها، ثبت و لغو نوبت، یادداشت' },
  { key: 'workhours', icon: Clock, title: 'ساعت کاری', subtitle: 'دیدن و تغییر ساعت کاری مطب' },
  { key: 'vacation', icon: CalendarOff, title: 'مرخصی', subtitle: 'ثبت و حذف مرخصی' },
];

/** «منشی دکتر …»: the doctor's own name, read through the gateway (the profile call is routed to the doctor while acting). */
const useActingDoctorName = () => {
  const doctorId = useActingDoctor();
  const profile = useQuery(['secretary-acting-doctor', doctorId], () => getDoctorProfile(), { enabled: !!doctorId, staleTime: 10 * 60_000, retry: false });
  const name = [profile.data?.name, profile.data?.family].filter(Boolean).join(' ').trim();
  return name || null;
};

const withDoctorPrefix = (name: string) => (/^دکتر/.test(name) ? name : `دکتر ${name}`);

/** Whose work this is and in which center: the first thing a secretary reads, and the place to change either. */
const SecretaryContext = () => {
  const user = useUserInfoStore(state => state.info);
  const name = useActingDoctorName();
  const { doctors } = useSecretarySession(true);
  const acting = useActingDoctor();
  const [doctorSheet, setDoctorSheet] = useState(false);
  const selectedCenterId = useSelectedCenterStore(state => state.selectedCenterId);
  const setSelectedCenterId = useSelectedCenterStore(state => state.setSelectedCenterId);
  const centers = getDoctorCenterOptions(user)
    .filter(center => center.id !== null)
    .map(center => ({ id: center.id as string, name: center.name, address: center.addressHint }));
  const options = centers.length > 1 ? [{ id: 'all', name: 'همهٔ مراکز', address: null }, ...centers] : centers;
  const many = doctors.length > 1;

  return (
    <DsCard padding="none" className="overflow-hidden">
      <button
        type="button"
        disabled={!many}
        onClick={() => setDoctorSheet(true)}
        className="flex w-full items-center gap-3 px-4 py-3 text-start active:bg-black/[0.04] disabled:active:bg-transparent"
      >
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Stethoscope size={22} aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className={classNames(ds.type.caption, 'block')}>شما منشی این پزشک هستید</span>
          <span className={classNames(ds.type.cardTitle, 'block truncate')}>{name ? withDoctorPrefix(name) : 'پزشک شما'}</span>
        </span>
        {many && <ChevronLeft size={18} className="shrink-0 text-slate-400" aria-hidden />}
      </button>
      {options.length > 0 && (
        <div className="border-t border-black/[0.06] p-2">
          <PanelCenterSelect centers={options} selectedId={selectedCenterId ?? 'all'} onSelect={id => setSelectedCenterId(id === 'all' ? null : id)} />
        </div>
      )}
      <DsDrawer open={doctorSheet} onOpenChange={setDoctorSheet} title="انتخاب پزشک" level={1}>
        <div className="px-4 pb-4" dir="rtl">
          <h2 className="pb-3 pt-1 text-[18px] font-bold text-[#222]">برای کدام پزشک کار می‌کنید؟</h2>
          <ul>
            {doctors.map(doctor => {
              const active = doctor.id === acting;
              return (
                <li key={doctor.id} className="border-b border-black/[0.06] last:border-0">
                  <button
                    type="button"
                    onClick={() => {
                      setDoctorSheet(false);
                      if (active) return;
                      setActingDoctor(doctor.id, doctor.scopes);
                      clearDoctorHomeShell();
                      clearPersistedSelectedCenter();
                      // the doctor's centers, name and cached pages all belong to the previous doctor: start over with the new one
                      window.location.assign('/_/');
                    }}
                    className="flex min-h-[52px] w-full items-center justify-between gap-3 py-3 text-start active:bg-gray-50"
                  >
                    <span className={classNames('block break-words text-[15px]', active ? 'font-semibold text-primary' : 'font-medium text-[#222]')}>
                      {doctor.name ? withDoctorPrefix(doctor.name) : `پزشک شمارهٔ ${doctor.id}`}
                    </span>
                    {active ? <Check size={20} className="shrink-0 text-primary" aria-hidden /> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </DsDrawer>
    </DsCard>
  );
};

/** One thing the secretary may do: a row on the home and the sheet it opens (the doctor's own pages, same as the doctor's home). */
const ActionRow = ({ action, first }: { action: (typeof ACTIONS)[number]; first: boolean }) => {
  const sheet = useSheetRoute(action.key);
  const drawerProps = useSheetDrawerProps(sheet);
  const Page = SHEET_PAGES[action.key];
  return (
    <>
      <button
        type="button"
        onClick={() => sheet.openSheet()}
        className={classNames('flex w-full items-center gap-3 px-4 py-3 text-start active:bg-black/[0.04]', !first && 'border-t border-black/[0.06]')}
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <action.icon size={20} aria-hidden />
        </span>
        <span className="min-w-0 flex-1">
          <span className={classNames(ds.type.cardTitle, 'block truncate')}>{action.title}</span>
          <span className={classNames(ds.type.caption, 'mt-0.5 block truncate')}>{action.subtitle}</span>
        </span>
        <ChevronLeft size={18} className="shrink-0 text-slate-400" aria-hidden />
      </button>
      <DsDrawer {...drawerProps} title={NATIVE_PANEL_TITLES[action.key]} fullHeight>
        {drawerProps.open ? (
          <div className="max-h-[88svh] overflow-y-auto bg-[#f7f7f8]" dir="rtl">
            <Page />
          </div>
        ) : null}
      </DsDrawer>
    </>
  );
};

/** What this secretary may do for the doctor, as the same kind of rows the doctor's home uses. */
const SecretaryActions = () => {
  const scopes = useActingScopes();
  const allowed = ACTIONS.filter(action => {
    const scope = PANEL_KEY_SCOPE[action.key as NativePanelKey];
    return !!scope && scopes.includes(scope);
  });
  if (allowed.length === 0) return null;
  return (
    <section dir="rtl" aria-label="کارهای شما">
      <DsSectionHeader title="کارهای شما" />
      <DsCard padding="none" className="overflow-hidden">
        {allowed.map((action, index) => (
          <ActionRow key={action.key} action={action} first={index === 0} />
        ))}
      </DsCard>
    </section>
  );
};

/** the day's appointments only — no online-visit switch, wallet, reviews or tools (not part of a secretary's access) */
const SecretaryToday = () => {
  const scopes = useActingScopes();
  const selectedDate = useSelectedDateStore(state => state.selectedDate);
  if (!scopes.includes('appointments')) return null;
  const selectedMoment = moment(selectedDate, 'YYYY-MM-DD');
  const title = selectedMoment.isSame(moment(), 'day') ? 'برنامه امروز' : `برنامه ${selectedMoment.clone().locale('fa').format('jD jMMMM')}`;
  return (
    <section dir="rtl" aria-label={title}>
      <DsSectionHeader title={title} />
      <DsCard padding="none" className="overflow-hidden">
        <FeedAppointmentsSection nested />
      </DsCard>
    </section>
  );
};

/** The doctor home as a secretary sees it: the same layout and cards, with only what her access opens. */
export const SecretaryHomeFeed = ({ className }: { className?: string }) => (
  <main aria-label="صفحه اصلی منشی" className={classNames('flex flex-col', ds.layout.feedSectionGap, className)}>
      <FeedMotion index={0}>
        <SecretaryContext />
      </FeedMotion>
      <FeedMotion index={1}>
        <FeedGreeting hideStats />
      </FeedMotion>
      <FeedMotion index={2}>
        <SecretaryActions />
      </FeedMotion>
      <FeedMotion index={3}>
        <SecretaryToday />
      </FeedMotion>
  </main>
);
